import { useEffect, useState } from 'react';
import { Menu as MenuIcon, X } from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import SidebarNavItem from '@/components/patterns/SidebarNavItem.jsx';
import PieveWordmark from '@/components/surfaces/PieveWordmark.jsx';
import AccountMenu from './AccountMenu.jsx';
import APP_NAVIGATION from './APP_NAVIGATION.js';
import LinkRecipientGate from './LinkRecipientGate.jsx';

/**
 * The signed-in frame: sidebar with the sections and the account menu, and the current section on
 * the right. Below 768px the sidebar becomes a drawer opened from a top bar.
 */
const AppShell = () => {
  const location = useLocation();
  // The drawer belongs to the page it was opened on, so following a link closes it.
  const [drawerOpenedAt, setDrawerOpenedAt] = useState(null);
  const isDrawerOpen = drawerOpenedAt === location.pathname;
  const setIsDrawerOpen = (isOpen) => setDrawerOpenedAt(isOpen ? location.pathname : null);

  useEffect(() => {
    if (!isDrawerOpen) {
      return undefined;
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setDrawerOpenedAt(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isDrawerOpen]);

  return (
    <div className="comp-app-shell" data-drawer-open={isDrawerOpen ? 'true' : undefined}>
      <header className="comp-app-shell__topbar">
        <Link to="/" className="comp-app-shell__brand" aria-label="Pieve, assistant">
          <PieveWordmark height={22} />
        </Link>
        <Button variant="ghost" iconOnly iconStart={MenuIcon} label="Open navigation" onClick={() => setIsDrawerOpen(true)} />
      </header>
      <div className="comp-app-shell__scrim" onClick={() => setIsDrawerOpen(false)} aria-hidden="true" />
      <aside className="comp-app-shell__sidebar" aria-label="Main">
        <div className="comp-app-shell__sidebar-head">
          <Link to="/" className="comp-app-shell__brand" aria-label="Pieve, assistant">
            <PieveWordmark height={24} />
          </Link>
          <span className="comp-app-shell__drawer-close">
            <Button variant="ghost" iconOnly iconStart={X} label="Close navigation" onClick={() => setIsDrawerOpen(false)} />
          </span>
        </div>
        <nav className="comp-app-shell__nav">
          {APP_NAVIGATION.map((item) => (
            <SidebarNavItem key={item.to} to={item.to} icon={item.icon} label={item.label} end={item.end} />
          ))}
        </nav>
        <div className="comp-app-shell__sidebar-foot">
          <AccountMenu />
        </div>
      </aside>
      <main className="comp-app-shell__main" id="main">
        <LinkRecipientGate>
          <Outlet />
        </LinkRecipientGate>
      </main>
    </div>
  );
};

export default AppShell;
